import type { Channel } from 'phoenix';
import type { OperatorFunction } from 'rxjs';
import { Observable, take } from 'rxjs';
import { map, switchMap, tap } from 'rxjs/operators';

type On<T, K> = T extends (event: K, callback: (arg?: infer R) => void) => number ? R : never;

export const fromChannel = <T extends Channel, K extends Parameters<T['on']>[0]>(
  channel: T,
  eventName: K,
): Observable<On<T['on'], K>> =>
  new Observable<On<T['on'], K>>(observer => {
    const ref = channel.on(eventName, data => observer.next(data));
    return () => channel.off(eventName, ref);
  });

export const bufferUntil =
  <T>(trigger: () => Observable<any>): OperatorFunction<T, T[]> =>
  (source: Observable<T>): Observable<T[]> => {
    let buffer: T[] = [];
    return source.pipe(
      tap((val: T) => (buffer = [...buffer, val])),
      switchMap(() =>
        trigger().pipe(
          take(1),
          map(() => buffer),
        ),
      ),
      tap(() => (buffer = [])),
    );
  };
